import { AccessAttributeValueWithAttribute } from 'src/api/deinverband-api';
import { Id } from 'src/types/common';
import { CategoryAccess } from 'src/types/company';
import { Capability, GroupRole } from 'src/types/user';
import { unique } from 'src/utils/tools';
import { AnyAttributeValue, AttributeName, AttributeTypes } from './attributes';

// check if the attribute is of given type
const of = (value: AnyAttributeValue, name: AttributeName) =>
  ('attribute' in value && value.attribute?.name === name) ||
  // (attribute.attributeName === undefined || attribute.attributeName === name) &&
  value.name === name;

export const extractAttributeValues = <T extends keyof AttributeTypes>(
  name: T,
  values: AnyAttributeValue[]
): AttributeTypes[T][] =>
  values
    // attribute values without populated attribute are not filtered
    .filter((value) => of(value, name))
    .map((value) => JSON.parse(value.value) as AttributeTypes[T]);

export const extractUserGroupRoles = (
  values: AnyAttributeValue[]
): GroupRole[] => {
  // serialize to ensure distinctiveness
  const groupRoles = extractAttributeValues('GroupRolesMembership', values)
    .filter((value) => value.groupId !== null && value.roleId !== null)
    .map(({ groupId, roleId }) => `${groupId}.${roleId}`);

  return unique(groupRoles).map((value) => ({
    group: { id: value.split('.')[0] },
    role: { id: value.split('.')[1] }
  }));
};

export const extractUserCategories = (
  values: AnyAttributeValue[]
): CategoryAccess[] => {
  // serialize to ensure distinctiveness
  const categories = extractAttributeValues('CompanyCategoryAccess', values)
    .filter((value) => value.categoryId !== null)
    .map(({ companyId, categoryId }) => `${companyId}.${categoryId}`);

  return unique(categories).map((value) => ({
    company: { id: +value.split('.')[0] },
    category: { id: +value.split('.')[1] }
  }));
};

export const extractTenantAdmin = (
  values: AccessAttributeValueWithAttribute[]
): boolean => values.some((value) => value.attribute.name === 'Admin');

export const extractEditor = (
  editorId: Id | undefined,
  values: AnyAttributeValue[]
): boolean =>
  extractAttributeValues('Editor', values).some(
    (value) => value.editorId === editorId
  );

export const extractCapabilities = (
  values: AnyAttributeValue[]
): Capability[] =>
  extractAttributeValues('Capabilities', values).flatMap(
    (value) => value.capabilities as Capability[]
  );
